<script setup>
import { computed, ref, reactive, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';

const store = useStore();
const getters = useStoreGetters();

const configs = computed(
  () => getters['notificationEmailConfigs/getEmailConfigs'].value
);
const uiFlags = computed(
  () => getters['notificationEmailConfigs/getUIFlags'].value
);

const showDialog = ref(false);
const editingConfig = ref(null);
const testingId = ref(null);
const showPassword = ref(false);

const form = reactive({
  name: '',
  from_email: '',
  from_name: '',
  smtp_address: '',
  smtp_port: 587,
  smtp_login: '',
  smtp_password: '',
  smtp_authentication: 'login',
  smtp_domain: '',
  smtp_enable_starttls: true,
  smtp_enable_ssl: false,
});

onMounted(() => {
  store.dispatch('notificationEmailConfigs/get');
});

const handleNew = () => {
  editingConfig.value = null;
  Object.assign(form, {
    name: '',
    from_email: '',
    from_name: '',
    smtp_address: '',
    smtp_port: 587,
    smtp_login: '',
    smtp_password: '',
    smtp_authentication: 'login',
    smtp_domain: '',
    smtp_enable_starttls: true,
    smtp_enable_ssl: false,
  });
  showPassword.value = false;
  showDialog.value = true;
};

const handleEdit = config => {
  editingConfig.value = config;
  Object.assign(form, {
    name: config.name,
    from_email: config.from_email,
    from_name: config.from_name || '',
    smtp_address: config.smtp_address,
    smtp_port: config.smtp_port,
    smtp_login: config.smtp_login,
    smtp_password: '',
    smtp_authentication: config.smtp_authentication || 'login',
    smtp_domain: config.smtp_domain || '',
    smtp_enable_starttls: config.smtp_enable_starttls,
    smtp_enable_ssl: config.smtp_enable_ssl,
  });
  showPassword.value = false;
  showDialog.value = true;
};

const handleClose = () => {
  showDialog.value = false;
  editingConfig.value = null;
};

const isFormValid = computed(() => {
  return (
    form.name.trim() &&
    form.from_email.trim() &&
    form.smtp_address.trim() &&
    form.smtp_port &&
    form.smtp_login.trim() &&
    (editingConfig.value || form.smtp_password.trim())
  );
});

const handleSubmit = async () => {
  if (!isFormValid.value) return;

  const data = { ...form };
  if (editingConfig.value && !data.smtp_password) {
    delete data.smtp_password;
  }

  try {
    if (editingConfig.value) {
      await store.dispatch('notificationEmailConfigs/update', {
        id: editingConfig.value.id,
        ...data,
      });
      useAlert('Configuração atualizada com sucesso');
    } else {
      await store.dispatch('notificationEmailConfigs/create', data);
      useAlert('Configuração criada com sucesso');
    }
    handleClose();
  } catch {
    useAlert('Erro ao salvar configuração');
  }
};

const handleDelete = async config => {
  try {
    await store.dispatch('notificationEmailConfigs/delete', config.id);
    useAlert('Configuração excluída');
  } catch {
    useAlert('Erro ao excluir');
  }
};

const handleTest = async config => {
  testingId.value = config.id;
  try {
    await store.dispatch('notificationEmailConfigs/test', config.id);
    useAlert('Conexão SMTP bem-sucedida!');
  } catch {
    useAlert('Falha na conexão SMTP. Verifique as configurações.');
  } finally {
    testingId.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 h-full p-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-n-slate-12">
        Configurações de E-mail
      </h1>
      <button
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
        @click="handleNew"
      >
        Nova Configuração
      </button>
    </div>

    <p class="mb-4 text-sm text-n-slate-11">
      Configure servidores SMTP para enviar e-mails junto com as notificações
      agendadas.
    </p>

    <div v-if="uiFlags.isFetching" class="flex items-center justify-center py-8">
      <span class="text-n-slate-11">Carregando...</span>
    </div>

    <div
      v-else-if="configs.length === 0"
      class="flex flex-col items-center justify-center py-16"
    >
      <div class="i-lucide-mail w-12 h-12 text-n-slate-9 mb-4" />
      <p class="text-n-slate-11">
        Nenhuma configuração de e-mail cadastrada
      </p>
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="text-xs uppercase bg-n-alpha-2 text-n-slate-11">
          <tr>
            <th class="px-4 py-3">Nome</th>
            <th class="px-4 py-3">E-mail</th>
            <th class="px-4 py-3">Servidor SMTP</th>
            <th class="px-4 py-3">Status</th>
            <th class="px-4 py-3">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="config in configs"
            :key="config.id"
            class="border-b border-n-strong"
          >
            <td class="px-4 py-3 font-medium text-n-slate-12">
              {{ config.name }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ config.from_email }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ config.smtp_address }}:{{ config.smtp_port }}
            </td>
            <td class="px-4 py-3">
              <span
                :class="config.enabled ? 'text-n-teal-11' : 'text-n-slate-9'"
                class="text-xs font-medium"
              >
                {{ config.enabled ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="px-4 py-3">
              <div class="flex gap-2">
                <button
                  :disabled="testingId === config.id"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-teal-11 disabled:opacity-50"
                  @click="handleTest(config)"
                >
                  {{ testingId === config.id ? 'Testando...' : 'Testar' }}
                </button>
                <button
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11"
                  @click="handleEdit(config)"
                >
                  Editar
                </button>
                <button
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-ruby-11"
                  @click="handleDelete(config)"
                >
                  Excluir
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Dialog -->
    <div
      v-if="showDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black-6"
      @click.self="handleClose"
    >
      <div
        class="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-n-solid-2 rounded-xl shadow-xl p-6"
      >
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold text-n-slate-12">
            {{ editingConfig ? 'Editar Configuração' : 'Nova Configuração' }}
          </h2>
          <button class="p-1 rounded hover:bg-n-alpha-2" @click="handleClose">
            <div class="i-lucide-x w-5 h-5 text-n-slate-11" />
          </button>
        </div>

        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">Nome *</label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Ex: E-mail Corporativo"
              class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                E-mail de Envio *
              </label>
              <input
                v-model="form.from_email"
                type="email"
                placeholder="noreply@empresa.com"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Nome de Exibição
              </label>
              <input
                v-model="form.from_name"
                type="text"
                placeholder="Empresa LTDA"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Servidor SMTP *
              </label>
              <input
                v-model="form.smtp_address"
                type="text"
                placeholder="smtp.gmail.com"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Porta *
              </label>
              <input
                v-model.number="form.smtp_port"
                type="number"
                placeholder="587"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Login SMTP *
              </label>
              <input
                v-model="form.smtp_login"
                type="text"
                placeholder="usuario@empresa.com"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Senha SMTP *
              </label>
              <div class="relative">
                <input
                  v-model="form.smtp_password"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="editingConfig ? 'Deixe vazio para manter' : 'Senha'"
                  class="w-full px-3 py-2 pr-10 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
                />
                <button
                  type="button"
                  class="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                  @click="showPassword = !showPassword"
                >
                  <div
                    :class="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    class="w-4 h-4 text-n-slate-10"
                  />
                </button>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Autenticação
              </label>
              <select
                v-model="form.smtp_authentication"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              >
                <option value="login">Login</option>
                <option value="plain">Plain</option>
                <option value="cram_md5">CRAM-MD5</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Domínio
              </label>
              <input
                v-model="form.smtp_domain"
                type="text"
                placeholder="empresa.com"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              />
            </div>
          </div>

          <div class="flex gap-6">
            <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
              <input
                v-model="form.smtp_enable_starttls"
                type="checkbox"
                class="accent-n-brand"
              />
              STARTTLS
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
              <input
                v-model="form.smtp_enable_ssl"
                type="checkbox"
                class="accent-n-brand"
              />
              SSL/TLS
            </label>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
            @click="handleClose"
          >
            Cancelar
          </button>
          <button
            :disabled="!isFormValid"
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
            @click="handleSubmit"
          >
            {{ editingConfig ? 'Salvar' : 'Criar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
